import React, { useState, useEffect, useRef } from 'react';
import {
  Upload,
  Download,
  Filter,
  Search,
  Edit2,
  Trash2,
  Check,
  X,
  FileText,
  AlertCircle,
  CheckCircle,
  Sparkles,
  Loader2
} from 'lucide-react';
import { useAuth } from '../../AuthContext';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, writeBatch } from 'firebase/firestore';
import { db, functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';
import {
  parseTransactionCSV,
  validateTransactions,
  exportTransactionsToCSV
} from '../../utils/budgeting/csvParser';
import { getDefaultCategories } from '../../utils/budgeting/categoryDefinitions';
import { formatCurrency } from '../../countryConfig';

const Transactions = ({ country, currency, onNavigate }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [transactions, setTransactions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [aiCategorizing, setAiCategorizing] = useState(false);
  const [aiProgress, setAiProgress] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (currentUser) {
      loadTransactions();
      loadCategories();
    }
  }, [currentUser]);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      const transactionsRef = collection(db, `users/${currentUser.uid}/transactions`);
      const snapshot = await getDocs(transactionsRef);

      const txns = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        txns.push({
          id: doc.id,
          ...data,
          date: data.date?.toDate?.() || new Date(data.date)
        });
      });

      // Sort by date descending
      txns.sort((a, b) => b.date - a.date);

      setTransactions(txns);
    } catch (error) {
      console.error('Error loading transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      // Always start with default categories
      const defaultCats = getDefaultCategories();

      // Load user's custom categories
      const categoriesRef = collection(db, `users/${currentUser.uid}/categories`);
      const snapshot = await getDocs(categoriesRef);

      const customCats = [];
      snapshot.forEach(doc => {
        customCats.push({ id: doc.id, ...doc.data() });
      });

      // Merge default and custom categories
      const allCategories = [...defaultCats, ...customCats];
      setCategories(allCategories);
    } catch (error) {
      console.error('Error loading categories:', error);
      setCategories(getDefaultCategories());
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setImporting(true);
      setImportProgress({ status: 'parsing', message: 'Parsing CSV file...' });

      // Parse CSV
      const result = await parseTransactionCSV(file);
      const { transactions: parsedTxns, errors } = result;

      if (errors && errors.length > 0) {
        console.warn('CSV parsing warnings:', errors);
      }

      setImportProgress({ status: 'validating', message: 'Validating transactions...' });

      // Validate
      const validation = validateTransactions(parsedTxns);

      if (!validation.isValid) {
        alert(`Validation errors:\n${validation.errors.join('\n')}`);
        setImporting(false);
        setImportProgress(null);
        return;
      }

      if (validation.warnings.length > 0) {
        console.warn('Validation warnings:', validation.warnings);
      }

      setImportProgress({
        status: 'categorizing',
        message: `Categorizing ${parsedTxns.length} transactions...`
      });

      // Simple rule-based categorization
      const categorizedTxns = parsedTxns.map(txn => ({
        ...txn,
        categoryId: categorizTransaction(txn.description, txn.amount),
        categorizedBy: 'rule'
      }));

      setImportProgress({ status: 'saving', message: 'Saving to database...' });

      // Batch save to Firestore
      const batch = writeBatch(db);
      categorizedTxns.forEach(txn => {
        const docRef = doc(collection(db, `users/${currentUser.uid}/transactions`));
        batch.set(docRef, {
          ...txn,
          userId: currentUser.uid,
          importedAt: new Date()
        });
      });

      await batch.commit();

      setImportProgress({
        status: 'complete',
        message: `Successfully imported ${parsedTxns.length} transactions`
      });

      // Reload transactions
      await loadTransactions();

      // Reset after 2 seconds
      setTimeout(() => {
        setImportProgress(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }, 2000);

    } catch (error) {
      console.error('Error importing transactions:', error);
      setImportProgress({
        status: 'error',
        message: `Import failed: ${error.message}`
      });
      setTimeout(() => setImportProgress(null), 3000);
    } finally {
      setImporting(false);
    }
  };

  const categorizTransaction = (description, amount) => {
    const descLower = description.toLowerCase();

    // Income patterns
    if (amount > 0 && (descLower.includes('salary') || descLower.includes('payroll') || descLower.includes('wage'))) {
      return 'primary-salary';
    }

    // Groceries
    if (descLower.includes('tesco') || descLower.includes('sainsbury') || descLower.includes('asda') ||
        descLower.includes('walmart') || descLower.includes('target') || descLower.includes('grocery')) {
      return 'groceries';
    }

    // Fuel
    if (descLower.includes('shell') || descLower.includes('bp') || descLower.includes('esso') ||
        descLower.includes('gas station') || descLower.includes('fuel') || descLower.includes('petrol')) {
      return 'fuel';
    }

    // Dining
    if (descLower.includes('restaurant') || descLower.includes('cafe') || descLower.includes('mcdonald') ||
        descLower.includes('kfc') || descLower.includes('pizza') || descLower.includes('starbucks')) {
      return 'eating-out';
    }

    // Subscriptions
    if (descLower.includes('netflix') || descLower.includes('spotify') || descLower.includes('amazon prime') ||
        descLower.includes('subscription') || descLower.includes('membership')) {
      return 'streaming-services';
    }

    // Transport
    if (descLower.includes('uber') || descLower.includes('lyft') || descLower.includes('taxi') ||
        descLower.includes('train') || descLower.includes('bus')) {
      return 'public-transport';
    }

    // Default to uncategorized
    return 'uncategorized';
  };

  const handleUpdateCategory = async (transactionId, newCategoryId) => {
    try {
      const txnRef = doc(db, `users/${currentUser.uid}/transactions`, transactionId);
      await updateDoc(txnRef, {
        categoryId: newCategoryId,
        categorizedBy: 'user'
      });

      await loadTransactions();
    } catch (error) {
      console.error('Error updating transaction:', error);
      alert('Failed to update transaction');
    }
  };

  const handleAICategorization = async () => {
    // Get uncategorized transactions
    const uncategorized = transactions.filter(
      txn => !txn.categoryId || txn.categoryId === 'uncategorized'
    );

    if (uncategorized.length === 0) {
      alert('No uncategorized transactions found!');
      return;
    }

    if (uncategorized.length > 50) {
      alert(`You have ${uncategorized.length} uncategorized transactions. AI can categorize up to 50 per hour. Only the first 50 will be processed.`);
    }

    const toProcess = uncategorized.slice(0, 50);

    try {
      setAiCategorizing(true);
      setAiProgress({ current: 0, total: toProcess.length, message: 'Starting AI categorization...' });

      const categorizeTransaction = httpsCallable(functions, 'categorizeTransaction');
      const batch = writeBatch(db);
      let successCount = 0;
      let failCount = 0;

      for (let i = 0; i < toProcess.length; i++) {
        const txn = toProcess[i];

        try {
          setAiProgress({
            current: i + 1,
            total: toProcess.length,
            message: `Categorizing: ${txn.description.substring(0, 30)}...`
          });

          // Call AI function
          const result = await categorizeTransaction({
            description: txn.description,
            amount: txn.amount,
            categories: categories.map(cat => ({
              id: cat.id,
              name: cat.name,
              type: cat.type
            }))
          });

          if (result.data && result.data.categoryId) {
            const txnRef = doc(db, `users/${currentUser.uid}/transactions`, txn.id);
            batch.update(txnRef, {
              categoryId: result.data.categoryId,
              categorizedBy: 'ai',
              aiConfidence: result.data.confidence || null
            });
            successCount++;
          } else {
            failCount++;
          }

          // Small delay to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 100));

        } catch (error) {
          console.error(`Error categorizing transaction ${txn.id}:`, error);
          failCount++;

          if (error.code === 'functions/resource-exhausted') {
            alert('Rate limit exceeded. You can categorize 50 transactions per hour. Please try again later.');
            break;
          }
        }
      }

      // Commit all updates
      if (successCount > 0) {
        await batch.commit();
      }

      setAiProgress({
        current: toProcess.length,
        total: toProcess.length,
        message: `Complete! Categorized ${successCount} transactions. ${failCount} failed.`
      });

      // Reload transactions
      await loadTransactions();

      // Clear progress after 3 seconds
      setTimeout(() => {
        setAiProgress(null);
      }, 3000);

    } catch (error) {
      console.error('Error in AI categorization:', error);
      alert(`AI categorization failed: ${error.message}`);
      setAiProgress(null);
    } finally {
      setAiCategorizing(false);
    }
  };

  const handleDeleteTransaction = async (transactionId) => {
    if (!window.confirm('Delete this transaction?')) return;

    try {
      const txnRef = doc(db, `users/${currentUser.uid}/transactions`, transactionId);
      await deleteDoc(txnRef);
      await loadTransactions();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      alert('Failed to delete transaction');
    }
  };

  const handleExport = () => {
    const categoryMap = {};
    categories.forEach(cat => {
      categoryMap[cat.id] = cat;
    });

    const csvData = exportTransactionsToCSV(transactions, categoryMap);
    const blob = new Blob([csvData], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `planlens-transactions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filteredTransactions = transactions.filter(txn => {
    if (searchQuery && !txn.description.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (filterCategory && txn.categoryId !== filterCategory) {
      return false;
    }
    return true;
  });

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat ? cat.name : 'Uncategorized';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Transactions</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Import and categorize your transactions
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload className="w-4 h-4" />
            Import CSV
          </button>
          <button
            onClick={handleAICategorization}
            disabled={aiCategorizing || transactions.filter(t => !t.categoryId || t.categoryId === 'uncategorized').length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {aiCategorizing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            AI Categorize
          </button>
          <button
            onClick={handleExport}
            disabled={transactions.length === 0}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept=".csv"
        onChange={handleFileUpload}
        className="hidden"
      />

      {/* Import Progress */}
      {importProgress && (
        <div className={`rounded-lg p-4 ${
          importProgress.status === 'complete' ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' :
          importProgress.status === 'error' ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' :
          'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
        }`}>
          <div className="flex items-center gap-3">
            {importProgress.status === 'complete' ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : importProgress.status === 'error' ? (
              <AlertCircle className="w-5 h-5 text-red-600" />
            ) : (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
            )}
            <p className={`text-sm font-medium ${
              importProgress.status === 'complete' ? 'text-green-900 dark:text-green-100' :
              importProgress.status === 'error' ? 'text-red-900 dark:text-red-100' :
              'text-blue-900 dark:text-blue-100'
            }`}>
              {importProgress.message}
            </p>
          </div>
        </div>
      )}

      {/* AI Categorization Progress */}
      {aiProgress && (
        <div className="rounded-lg p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 border border-purple-200 dark:border-purple-800">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              {aiProgress.current === aiProgress.total ? (
                <CheckCircle className="w-5 h-5 text-purple-600" />
              ) : (
                <Sparkles className="w-5 h-5 text-purple-600 animate-pulse" />
              )}
              <p className="text-sm font-medium text-purple-900 dark:text-purple-100">
                {aiProgress.message}
              </p>
            </div>
            <span className="text-xs text-purple-700 dark:text-purple-300">
              {aiProgress.current}/{aiProgress.total}
            </span>
          </div>
          <div className="w-full bg-purple-200 dark:bg-purple-800 rounded-full h-2">
            <div
              className="bg-gradient-to-r from-purple-600 to-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(aiProgress.current / aiProgress.total) * 100}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Uncategorized Summary */}
      {transactions.length > 0 && (() => {
        const uncategorized = transactions.filter(t => !t.categoryId || t.categoryId === 'uncategorized');
        if (uncategorized.length > 0) {
          return (
            <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                  <div>
                    <p className="text-sm font-medium text-orange-900 dark:text-orange-100">
                      {uncategorized.length} uncategorized transaction{uncategorized.length !== 1 ? 's' : ''}
                    </p>
                    <p className="text-xs text-orange-700 dark:text-orange-300 mt-0.5">
                      Click "AI Categorize" to automatically categorize using Gemini AI
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleAICategorization}
                  disabled={aiCategorizing}
                  className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-sm rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all disabled:opacity-50 flex items-center gap-2"
                >
                  <Sparkles className="w-3 h-3" />
                  Categorize Now
                </button>
              </div>
            </div>
          );
        }
        return null;
      })()}

      {/* CSV Template Info */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
          <div>
            <p className="font-medium text-blue-900 dark:text-blue-100 mb-1">CSV Format</p>
            <p className="text-sm text-blue-800 dark:text-blue-200">
              Upload a CSV file with columns: <strong>Date, Description, Amount</strong>
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
              Dates: YYYY-MM-DD, DD/MM/YYYY, or MM/DD/YYYY • Amounts: Use negative for expenses
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search transactions..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="sm:w-64">
          <select
            value={filterCategory}
            onChange={(e) => setFilterCategory(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Transactions Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {filteredTransactions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredTransactions.map(txn => (
                  <tr key={txn.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {txn.date.toLocaleDateString('en-GB')}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                      {txn.description}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <select
                        value={txn.categoryId || ''}
                        onChange={(e) => handleUpdateCategory(txn.id, e.target.value)}
                        className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Uncategorized</option>
                        {categories.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                      </select>
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${
                      txn.amount >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(txn.amount, country)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                      <button
                        onClick={() => handleDeleteTransaction(txn.id)}
                        className="text-red-600 hover:text-red-800 dark:hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-12 text-center text-gray-500 dark:text-gray-400">
            <Upload className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="text-lg font-medium mb-2">No transactions yet</p>
            <p className="text-sm">Import a CSV file to get started</p>
          </div>
        )}
      </div>

      {/* Stats */}
      {filteredTransactions.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Transactions</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{filteredTransactions.length}</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Income</p>
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(
                filteredTransactions.filter(t => t.amount > 0).reduce((sum, t) => sum + t.amount, 0),
                country
              )}
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Total Expenses</p>
            <p className="text-2xl font-bold text-red-600">
              {formatCurrency(
                Math.abs(filteredTransactions.filter(t => t.amount < 0).reduce((sum, t) => sum + t.amount, 0)),
                country
              )}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default Transactions;
